import { useEffect, useState } from "react";
import { apiJson } from "../../utils/api";
import { CoupleCodeBar } from "./MarriageLayout";
import { getCoupleCode, getUserId, setCoupleCode } from "../../utils/coupleSession";
import useSeo from "../../hooks/useSeo";
import { addReminder } from "../../utils/reminderStorage";

const PRESETS = ["2026-05-20", "2026-07-07", "2026-09-09", "2026-10-10"];

export default function MarriageDate() {
  const [code, setCode] = useState(getCoupleCode());
  const [target, setTarget] = useState("2026-09-09");
  const [label, setLabel] = useState("我们的领证日");
  const [city, setCity] = useState("");
  const [items, setItems] = useState([]);
  const [places, setPlaces] = useState([]);
  const [hint, setHint] = useState("");
  const userId = getUserId();

  useSeo({
    title: "我的领证日",
    metaDescription: "为计划中的领证日期做倒计时、材料与地点提醒。",
    path: "/marriage/date",
  });

  const applyCode = (value) => setCode(setCoupleCode(value));

  const load = () => {
    if (!userId && !code) {
      setItems([]);
      return;
    }
    const q = new URLSearchParams();
    if (userId) q.set("user_id", String(userId));
    if (code) q.set("code", code);
    apiJson(`/api/marriage/dates?${q.toString()}`)
      .then(({ data }) => setItems(data.items || []))
      .catch(() => setItems([]));
  };

  useEffect(() => {
    load();
  }, [code, userId]);

  useEffect(() => {
    if (!city) return;
    apiJson(`/api/marriage/places?city=${encodeURIComponent(city)}`).then(({ data }) =>
      setPlaces((data.items || []).filter((p) => p.can_legal_register).slice(0, 3))
    );
  }, [city]);

  const save = async (e) => {
    e.preventDefault();
    if (!userId && !code) {
      setHint("请先登录网站账号。领证日按账号保存，纪念码可选用。");
      return;
    }
    const { res, data } = await apiJson("/api/marriage/dates", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        target_date: target,
        label,
        city,
        couple_code: code || "",
        user_id: userId || null,
      }),
    });
    if (!res.ok) {
      setHint(data?.detail || "保存失败，请先登录网站账号");
      return;
    }
    setHint("已按你的网站账号保存领证日提醒。");
    (data.itinerary || [])
      .filter((step) => step.title !== "倒计时")
      .forEach((step) => {
        const when = new Date(`${target}T09:00:00`);
        when.setDate(when.getDate() + (step.offset_days || 0));
        if (when.getTime() > Date.now()) {
          addReminder({
            content: `${label} · ${step.title}：${step.detail}`,
            remindAt: when.toISOString(),
            sourceText: "领证日行程",
          });
        }
      });
    load();
  };

  return (
    <div className="space-y-6 max-w-3xl">
      <h1 className="text-3xl font-extrabold">📅 我的领证日</h1>
      <p className="text-sm text-gray-600">
        领证日跟网站登录账号绑定。登录后即可保存倒计时；情侣纪念码可选，方便和另一半一起看行程。
      </p>
      {!userId ? (
        <p className="text-sm text-amber-800 bg-amber-50 rounded-xl px-3 py-2">
          你还没登录。请先登录网站账号，再用这个页面保存领证日。
        </p>
      ) : (
        <p className="text-xs text-gray-500">当前账号 ID：{userId}</p>
      )}
      <CoupleCodeBar code={code} onChange={applyCode} required={false} />
      {hint ? <p className="text-sm text-rose-700">{hint}</p> : null}
      <div className="flex flex-wrap gap-2">
        {PRESETS.map((d) => (
          <button key={d} type="button" onClick={() => setTarget(d)} className="px-3 py-1 rounded-full border text-sm">
            {d.slice(5)}
          </button>
        ))}
      </div>
      <form onSubmit={save} className="bg-white rounded-2xl p-5 grid sm:grid-cols-2 gap-3">
        <input type="date" value={target} onChange={(e) => setTarget(e.target.value)} className="border rounded-xl px-3 py-2" />
        <input value={label} onChange={(e) => setLabel(e.target.value)} placeholder="恋爱纪念日 / 自定义" className="border rounded-xl px-3 py-2" />
        <input value={city} onChange={(e) => setCity(e.target.value)} placeholder="计划领证城市" className="border rounded-xl px-3 py-2 sm:col-span-2" />
        <button className="sm:col-span-2 py-3 rounded-xl bg-rose-500 text-white">保存提醒</button>
      </form>
      {items.map((item) => (
        <div key={item.id} className="bg-white rounded-2xl p-6 text-center">
          <p className="text-sm text-gray-500">{item.label}</p>
          <p className="text-4xl font-extrabold text-rose-600 my-2">
            {item.days_left >= 0 ? `还有 ${item.days_left} 天` : `已过 ${Math.abs(item.days_left)} 天`}
          </p>
          <p>{item.target_date}{item.city ? ` · ${item.city}` : ""}</p>
          <ul className="text-left text-sm mt-4 space-y-2">
            {(item.itinerary || [])
              .filter((step) => step.title !== "倒计时")
              .map((step) => (
                <li key={step.title} className="bg-rose-50 rounded-xl p-3">
                  <b>{step.title}</b>
                  <p className="text-gray-600 mt-1">{step.detail}</p>
                </li>
              ))}
          </ul>
        </div>
      ))}
      <div className="bg-amber-50 rounded-2xl p-5 text-sm space-y-2">
        <p className="font-bold">材料提醒（全国通则，以当地为准）</p>
        <p>内地居民一般需双方亲自到场，带有效身份证，并签署无配偶及无近亲关系声明。2025年5月10日起通常不再要求户口簿。</p>
        <p>预约提醒：是否可周末办理、号源情况请提前查当地婚姻登记处公告。</p>
        {places.map((p) => (
          <p key={p.id}>
            登记地点参考：{p.name} · {p.opening_hours}
          </p>
        ))}
      </div>
    </div>
  );
}
